<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <oxd-text tag="h6" class="orangehrm-main-title"
        >2027 Handbook Policy</oxd-text
      >
      <oxd-divider />
      <oxd-text class="policy-description">
        Effective January 1, 2027. Annual vacation and up to five new Sick and
        Safe Leave days are available at the start of the year. New sick leave
        grants respect the configured balance cap. Vacation earnings are tracked
        separately. Sick leave carries forward; configure protected vacation
        carryover for employees whose unused earned vacation must be retained.
        Existing 2026 allocations remain in their original leave year.
      </oxd-text>
      <oxd-text v-if="error" class="policy-error" role="alert">{{
        error
      }}</oxd-text>
      <oxd-button
        v-if="!hasLoaded && error"
        label="Try again"
        display-type="ghost"
        :disabled="loading"
        @click="loadConfiguration"
      />
      <oxd-text v-if="!hasLoaded && loading"
        >Loading handbook settings…</oxd-text
      >
      <oxd-form v-if="hasLoaded" :loading="loading" @submit-valid="save">
        <oxd-text type="card-title"
          >Automatic approval for ordinary leave</oxd-text
        >
        <oxd-form-row>
          <oxd-input-field
            v-model="selectedAutoApprovalTitles"
            type="multiselect"
            label="Job titles with automatic leave approval"
            :options="availableJobTitles"
          />
        </oxd-form-row>
        <oxd-text class="policy-description">
          This setting takes effect immediately when saved, including before
          2027 and when the 2027 handbook rules are disabled. New ordinary leave
          requests submitted by employees with these exact job titles are
          approved automatically after the usual eligibility, balance, notice
          and conflict checks. Existing pending requests and remote work
          applications keep their approval process. Leave this list empty to
          turn automatic approval off.
        </oxd-text>
        <oxd-divider />
        <oxd-form-row>
          <oxd-input-field
            v-model="configuration.enabled"
            type="switch"
            label="Enable 2027 handbook rules"
          />
        </oxd-form-row>
        <oxd-text type="card-title"
          >Eligible full-time employment statuses</oxd-text
        >
        <oxd-text class="policy-description">
          Select the statuses that qualify for paid benefits. No staff receive
          automatic grants until eligibility and leave types are configured.
        </oxd-text>
        <oxd-form-row>
          <oxd-input-field
            v-model="selectedStatuses"
            type="multiselect"
            label="Eligible employment statuses"
            :options="availableEmploymentStatuses"
          />
        </oxd-form-row>
        <oxd-text type="card-title">Executive and director job titles</oxd-text>
        <oxd-text class="policy-description">
          These titles earn 15 days annually on hire and 20 after two years.
          Other eligible staff earn 10 on hire, 15 after two years and 20 after
          eight.
        </oxd-text>
        <oxd-form-row>
          <oxd-input-field
            v-model="selectedDirectorTitles"
            type="multiselect"
            label="Executive and director titles"
            :options="availableJobTitles"
          />
        </oxd-form-row>
        <oxd-text type="card-title">Leave types and approvals</oxd-text>
        <oxd-form-row>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item v-for="field in typeFields" :key="field.key">
              <oxd-input-field
                v-model="selectedTypes[field.key]"
                type="select"
                :label="field.label"
                :options="availableLeaveTypes"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="executiveDirector"
                type="select"
                label="Executive Director approver"
                :options="availableEmployees"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="selectedRemoteReviewers"
                type="multiselect"
                label="Additional remote work reviewers (optional)"
                :options="remoteReviewerOptions"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="configuration.hoursPerDay"
                label="Hours per leave day"
                :rules="positiveRules"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="configuration.personalDaysPerYear"
                label="Personal days per year"
                :rules="nonNegativeRules"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="configuration.sickBalanceCapHours"
                label="Sick leave balance cap (hours, optional)"
                :rules="sickCapRules"
                placeholder="64"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-text class="policy-description">
          Additional remote work reviewers approve after the supervisor and
          before the Executive Director. Leave this selection empty to use
          supervisor review and Executive Director approval only.
        </oxd-text>
        <oxd-form-row>
          <oxd-input-field
            v-model="configuration.vacationFrontLoaded"
            type="switch"
            label="Make annual vacation available at the start of the year"
          />
        </oxd-form-row>
        <oxd-text class="policy-description">
          Staff can use the posted annual grant while vacation earnings accrue
          separately. This does not permit use beyond the available grant.
          Turning this off restores monthly vacation credits.
        </oxd-text>
        <oxd-form-row>
          <oxd-input-field
            v-model="selectedCarryoverEmployees"
            type="multiselect"
            label="Employees with protected vacation carryover"
            :options="availableSeparationEmployees"
          />
        </oxd-form-row>
        <oxd-text class="policy-description">
          Select employees whose unused earned vacation must carry forward,
          including California employees. This is an explicit HR designation;
          the system does not infer jurisdiction from an address. Other vacation
          expires at year-end under the handbook policy. Only earned vacation,
          not the unused portion of an annual advance, carries forward.
        </oxd-text>
        <oxd-text class="policy-description">
          Sick and Safe Leave remains an annual grant of up to five new days. If
          a balance cap is set, carryover and new credits are limited to the
          available room in the sick leave bank. Using sick leave during the
          year does not trigger another annual grant. Leave the cap blank for no
          balance limit. Sick leave is not subject to repayment. Vacation
          earnings account for partial employment months and service
          anniversaries. Bereavement provides ten days per year with no
          carryover. Personal days default to zero until management sets the
          allowance.
        </oxd-text>
        <oxd-text class="policy-description">
          Jury/witness duty and military leave have no annual quota.
          Discretionary days must be credited by an administrator for the week
          following eligible weekend work. Check Leave → Holidays for the annual
          calendar; Christmas Eve and observed Christmas Day coincide on
          December 24, 2027. Add any management-approved substitute holiday
          there.
        </oxd-text>
        <oxd-text class="policy-description">
          For leave dated 2027 onward, the configured Executive Director
          finalizes requests in Leave → Leave Approvals, except new requests
          covered by automatic approval above. Employees cannot manually approve
          their own requests. Remote work requires supervisor review followed by
          Executive Director approval.
        </oxd-text>
        <oxd-divider />
        <oxd-form-actions>
          <required-text />
          <submit-button label="Save" :disabled="loading || previewLoading" />
        </oxd-form-actions>
      </oxd-form>
    </div>

    <div v-if="hasLoaded" class="orangehrm-paper-container policy-preview">
      <div class="orangehrm-card-container">
        <oxd-text tag="h6" class="orangehrm-main-title"
          >Preview leave credits</oxd-text
        >
        <oxd-text class="policy-description">
          Preview uses the saved policy and employee records. Save changes above
          first if you want to include them. Previewing does not save settings
          or change anyone's leave allocation.
        </oxd-text>
        <oxd-form :loading="previewLoading" @submit-valid="runPreview">
          <oxd-form-row>
            <oxd-grid :cols="3" class="orangehrm-full-width-grid">
              <oxd-grid-item>
                <date-input
                  v-model="previewDate"
                  label="Calculate credits as of"
                  :rules="previewDateRules"
                  required
                />
              </oxd-grid-item>
            </oxd-grid>
          </oxd-form-row>
          <oxd-form-actions>
            <submit-button
              label="Preview Saved Policy"
              :disabled="loading || previewLoading"
            />
          </oxd-form-actions>
        </oxd-form>
        <oxd-text v-if="previewError" class="policy-error" role="alert">{{
          previewError
        }}</oxd-text>
        <template v-if="preview && !previewLoading">
          <oxd-text class="policy-description">
            Preview as of {{ preview.date }}. Allocations are entitlement totals
            before subtracting leave already used; displayed amounts are rounded
            to four decimal places.
          </oxd-text>
          <oxd-text
            v-if="preview.processed.length === 0"
            class="policy-description"
          >
            {{
              !preview.enabled
                ? 'The saved handbook policy is disabled, so no credits would be processed.'
                : 'No eligible leave credits would be processed for this date. Check the effective date, saved eligibility settings, and employee hire dates.'
            }}
          </oxd-text>
          <div
            v-if="preview.skipped.length"
            class="policy-skipped"
            role="status"
          >
            <oxd-text type="card-title">Employees needing review</oxd-text>
            <oxd-text v-for="item in preview.skipped" :key="item.empNumber">
              {{ employeeName(item.empNumber) }}: {{ item.reason }}
            </oxd-text>
          </div>
        </template>
      </div>
      <template v-if="preview && preview.processed.length && !previewLoading">
        <table-header
          :selected="0"
          :total="preview.processed.length"
          :loading="false"
        />
        <div class="orangehrm-container">
          <oxd-card-table
            :headers="previewHeaders"
            :items="previewRows"
            :selectable="false"
            :clickable="false"
            row-decorator="oxd-table-decorator-card"
          />
        </div>
        <div class="orangehrm-bottom-container">
          <oxd-pagination
            v-if="previewPages > 1"
            v-model:current="previewPage"
            :length="previewPages"
          />
        </div>
      </template>
    </div>
    <div v-if="hasLoaded" class="orangehrm-card-container policy-preview">
      <oxd-text tag="h6" class="orangehrm-main-title"
        >Separation review</oxd-text
      >
      <oxd-text class="policy-description">
        Review an employee's earned vacation and actual leave taken through
        today or an earlier date. This uses saved settings. It does not save
        settings, change balances, or make a payroll deduction.
      </oxd-text>
      <oxd-form :loading="reviewLoading" @submit-valid="runSeparationReview">
        <oxd-form-row>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="reviewEmployee"
                type="select"
                label="Employee"
                :options="availableSeparationEmployees"
                :rules="requiredRules"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <date-input
                v-model="reviewDate"
                label="Review through"
                :rules="previewDateRules"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-form-actions>
          <submit-button
            label="Review Saved Records"
            :disabled="loading || reviewLoading"
          />
        </oxd-form-actions>
      </oxd-form>
      <oxd-text v-if="reviewError" class="policy-error" role="alert">{{
        reviewError
      }}</oxd-text>
      <template v-if="separationReview && !reviewLoading">
        <oxd-divider />
        <oxd-text type="card-title">{{ reviewedEmployeeName }}</oxd-text>
        <handbook-balance :summary="separationReview" separation />
      </template>
    </div>
  </div>
</template>

<script>
import {APIService} from '@/core/util/services/api.service';
import useDateFormat from '@/core/util/composable/useDateFormat';
import {required, validDateFormat} from '@/core/util/validation/rules';
import HandbookBalance from '@/orangehrmLeavePlugin/components/HandbookBalance.vue';

const policyPath = '/api/v2/leave/handbook-policy';

export default {
  name: 'HandbookPolicy',
  components: {HandbookBalance},
  setup() {
    const http = new APIService(window.appGlobal.baseUrl, policyPath);
    http.setIgnorePath(policyPath);
    const {userDateFormat} = useDateFormat();
    return {http, userDateFormat};
  },
  data() {
    return {
      loading: false,
      hasLoaded: false,
      error: '',
      configuration: {},
      availableLeaveTypes: [],
      availableEmploymentStatuses: [],
      availableJobTitles: [],
      availableEmployees: [],
      availableSeparationEmployees: [],
      selectedStatuses: [],
      selectedDirectorTitles: [],
      selectedAutoApprovalTitles: [],
      selectedRemoteReviewers: [],
      selectedCarryoverEmployees: [],
      selectedTypes: {},
      executiveDirector: null,
      previewDate: '2027-01-01',
      previewLoading: false,
      previewError: '',
      preview: null,
      previewEmployees: [],
      previewPage: 1,
      previewPageSize: 25,
      reviewEmployee: null,
      reviewDate: new Date().toLocaleDateString('en-CA'),
      reviewLoading: false,
      reviewError: '',
      separationReview: null,
      reviewedEmployeeName: '',
      requiredRules: [required],
      typeFields: [
        {key: 'vacationLeaveTypeId', label: 'Vacation'},
        {key: 'sickLeaveTypeId', label: 'Sick and Safe Leave'},
        {key: 'bereavementLeaveTypeId', label: 'Bereavement'},
        {key: 'personalLeaveTypeId', label: 'Personal Day'},
        {key: 'juryLeaveTypeId', label: 'Jury/Witness Duty'},
        {key: 'militaryLeaveTypeId', label: 'Military Leave'},
        {key: 'discretionaryLeaveTypeId', label: 'Discretionary Day'},
      ],
      positiveRules: [
        required,
        (value) =>
          (Number(value) > 0 && Number(value) <= 24) ||
          'Enter hours greater than zero, up to 24.',
      ],
      nonNegativeRules: [
        required,
        (value) =>
          (Number(value) >= 0 && Number(value) <= 366) ||
          'Enter a number from 0 to 366.',
      ],
      sickCapRules: [
        (value) =>
          value == null ||
          String(value).trim() === '' ||
          (Number.isFinite(Number(value)) &&
            Number(value) > 0 &&
            Number(value) <= 8784) ||
          'Enter hours greater than zero, up to 8784, or leave blank.',
      ],
      previewDateRules: [required, validDateFormat(this.userDateFormat)],
      previewHeaders: [
        {name: 'employee', title: 'Employee', style: {flex: 1.5}},
        {name: 'year', title: 'Year', style: {flex: 0.75}},
        {name: 'category', title: 'Leave type', style: {flex: 1.5}},
        {name: 'earnedDays', title: 'Earned days', style: {flex: 1}},
        {name: 'grantedDays', title: 'New grant (days)', style: {flex: 1}},
        {name: 'carryoverDays', title: 'Carried days', style: {flex: 1}},
        {name: 'updatedDays', title: 'Allocation (days)', style: {flex: 1}},
        {name: 'result', title: 'Proposed change', style: {flex: 1.25}},
      ],
    };
  },
  computed: {
    remoteReviewerOptions() {
      return this.availableEmployees.filter(
        (employee) => employee.id !== this.executiveDirector?.id,
      );
    },
    previewPages() {
      return Math.ceil(
        (this.preview?.processed.length || 0) / this.previewPageSize,
      );
    },
    previewRows() {
      const categories = {
        vacation: 'Vacation',
        sick: 'Sick and Safe Leave',
        bereavement: 'Bereavement',
        personal: 'Personal Day',
      };
      const actions = {
        created: 'Would create',
        updated: 'Would update',
        unchanged: 'No change',
      };
      const offset = (this.previewPage - 1) * this.previewPageSize;
      return (this.preview?.processed || [])
        .slice(offset, offset + this.previewPageSize)
        .map((row) => ({
          employee: this.employeeName(row.empNumber),
          year: row.year,
          category: categories[row.kind] || row.kind,
          earnedDays: Number(row.earnedDays).toFixed(4),
          grantedDays: Number(row.grantedDays ?? row.earnedDays).toFixed(4),
          carryoverDays: Number(row.carryoverDays).toFixed(4),
          updatedDays: Number(row.updatedDays).toFixed(4),
          result: actions[row.action] || row.action,
        }));
    },
  },
  beforeMount() {
    this.loadConfiguration();
  },
  methods: {
    errorMessage(error, fallback) {
      return (
        error?.response?.data?.error?.message ||
        error?.data?.error?.message ||
        fallback
      );
    },
    async loadConfiguration() {
      this.loading = true;
      this.error = '';
      try {
        this.load((await this.http.getAll()).data.data);
        this.hasLoaded = true;
      } catch (error) {
        this.error = this.errorMessage(
          error,
          'Unable to load the handbook policy.',
        );
      } finally {
        this.loading = false;
      }
    },
    load(data) {
      this.configuration = data.configuration;
      this.configuration.vacationFrontLoaded =
        data.configuration.vacationFrontLoaded !== false;
      [
        'availableLeaveTypes',
        'availableEmploymentStatuses',
        'availableJobTitles',
        'availableEmployees',
      ].forEach((key) => {
        this[key] = data[key] || [];
      });
      this.availableSeparationEmployees =
        data.availableSeparationEmployees || this.availableEmployees;
      this.selectedCarryoverEmployees =
        this.availableSeparationEmployees.filter((employee) =>
          (this.configuration.vacationCarryoverEmpNumbers || []).includes(
            employee.id,
          ),
        );
      this.selectedStatuses = this.availableEmploymentStatuses.filter(
        (option) => this.configuration.fullTimeStatusIds.includes(option.id),
      );
      this.selectedDirectorTitles = this.availableJobTitles.filter((option) =>
        this.configuration.directorJobTitleIds.includes(option.id),
      );
      this.selectedAutoApprovalTitles = this.availableJobTitles.filter(
        (option) =>
          (this.configuration.autoApproveJobTitleIds || []).includes(option.id),
      );
      this.selectedRemoteReviewers = this.availableEmployees.filter(
        (employee) =>
          (this.configuration.remoteReviewerEmpNumbers || []).includes(
            employee.id,
          ),
      );
      this.typeFields.forEach(({key}) => {
        this.selectedTypes[key] =
          this.availableLeaveTypes.find(
            (option) => option.id === this.configuration[key],
          ) || null;
      });
      this.executiveDirector =
        this.availableEmployees.find(
          (option) =>
            option.id === this.configuration.executiveDirectorEmpNumber,
        ) || null;
    },
    async save() {
      if (this.loading || this.previewLoading || this.reviewLoading) return;
      this.error = '';
      this.loading = true;
      const configuration = {
        ...this.configuration,
        autoApproveJobTitleIds: this.selectedAutoApprovalTitles.map(
          (option) => option.id,
        ),
        sickAccrualFrequency: 'annual',
        allowAdvanceVacation: false,
        vacationCarryoverEmpNumbers: this.selectedCarryoverEmployees.map(
          (employee) => employee.id,
        ),
        fullTimeStatusIds: this.selectedStatuses.map((option) => option.id),
        remoteReviewerEmpNumbers: this.selectedRemoteReviewers
          .map((employee) => employee.id)
          .filter((id) => id !== this.executiveDirector?.id),
        directorJobTitleIds: this.selectedDirectorTitles.map(
          (option) => option.id,
        ),
        hoursPerDay: Number(this.configuration.hoursPerDay),
        sickBalanceCapHours:
          this.configuration.sickBalanceCapHours == null ||
          String(this.configuration.sickBalanceCapHours).trim() === ''
            ? null
            : Number(this.configuration.sickBalanceCapHours),
        personalDaysPerYear: Number(this.configuration.personalDaysPerYear),
        executiveDirectorEmpNumber: this.executiveDirector?.id || null,
      };
      this.typeFields.forEach(({key}) => {
        configuration[key] = this.selectedTypes[key]?.id || null;
      });
      try {
        const response = await this.http.request({
          method: 'PUT',
          data: {configuration},
        });
        this.load(response.data.data);
        this.preview = null;
        this.previewError = '';
        this.separationReview = null;
        this.reviewError = '';
        this.$toast.saveSuccess();
      } catch (error) {
        this.error = this.errorMessage(
          error,
          'Unable to save the handbook policy.',
        );
      } finally {
        this.loading = false;
      }
    },
    async runPreview() {
      if (this.loading || this.previewLoading) return;
      this.previewLoading = true;
      this.previewError = '';
      this.preview = null;
      try {
        const response = await this.http.getAll({
          previewDate: this.previewDate,
        });
        this.preview = response.data.data.preview;
        this.previewEmployees = response.data.data.availableEmployees;
        this.previewPage = 1;
      } catch (error) {
        this.previewError = this.errorMessage(
          error,
          'Preview could not be calculated. Check the saved eligibility settings, leave types, and employee records, then try again.',
        );
      } finally {
        this.previewLoading = false;
      }
    },
    employeeName(empNumber) {
      return (
        this.previewEmployees.find((employee) => employee.id === empNumber)
          ?.label || `Employee #${empNumber}`
      );
    },
    async runSeparationReview() {
      if (this.loading || this.reviewLoading || !this.reviewEmployee) return;
      this.reviewLoading = true;
      this.reviewError = '';
      this.separationReview = null;
      const selectedEmployee = this.reviewEmployee;
      try {
        const response = await this.http.getAll({
          balanceDate: this.reviewDate,
          separationEmpNumber: selectedEmployee.id,
        });
        this.separationReview = response.data.data.separationReview;
        this.reviewedEmployeeName = selectedEmployee.label;
      } catch (error) {
        this.reviewError = this.errorMessage(
          error,
          'The separation review could not be calculated. Check the employee, date and saved leave settings.',
        );
      } finally {
        this.reviewLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.policy-description {
  margin: 1rem 0;
  line-height: 1.6;
}
.policy-error {
  margin: 1rem 0;
  padding: 1rem;
  border-left: 3px solid $oxd-feedback-danger-color;
}
.policy-preview {
  margin-top: 1rem;
}
.policy-skipped {
  padding: 1rem;
  background: $oxd-background-pastel-white-color;
  .oxd-text + .oxd-text {
    margin-top: 0.5rem;
  }
}
</style>
